/**
 * Single source of truth for the VAD settings panel.
 * Technical keys are stable (backend contract); only display strings
 * target non-technical users — labels describe effects, not mechanics.
 */

export interface VadParameterDefinition {
  readonly key: string;
  readonly label: string;
  readonly description: string;
  readonly sliderMinLabel: string;
  readonly sliderMaxLabel: string;
  readonly unit?: string;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly defaultValue: number;
  readonly decimals: number;
}

export type VadSectionId = 'volume' | 'silero';

export interface VadSectionDefinition {
  readonly id: VadSectionId;
  readonly title: string;
  readonly description: string;
  readonly parameters: readonly VadParameterDefinition[];
}

export type VadValues = Record<string, number>;

export const VOLUME_VAD_SECTION: VadSectionDefinition = {
  id: 'volume',
  title: 'Détection par le volume',
  description: 'Premier filtre — réagit à la force des sons',
  parameters: [
    {
      key: 'volume.history',
      label: 'Stabilité du son',
      description:
        'Lisse le volume mesuré pour éviter les réactions en dents de scie. Vers la gauche, la cabine réagit vite ; vers la droite, elle est plus stable mais un peu plus lente.',
      sliderMinLabel: 'Réagit vite',
      sliderMaxLabel: 'Plus stable',
      min: 1,
      max: 30,
      step: 1,
      defaultValue: 5,
      decimals: 0,
    },
    {
      key: 'volume.threshold',
      label: 'Niveau de déclenchement',
      description:
        "Le niveau sonore à partir duquel la cabine considère que quelqu'un parle. Vers la gauche, elle entend même les voix faibles ou lointaines ; vers la droite, elle n'écoute que la personne proche du micro.",
      sliderMinLabel: 'Entend tout',
      sliderMaxLabel: 'Voix proche uniquement',
      unit: 'dB',
      min: -90,
      max: -20,
      step: 1,
      defaultValue: -55,
      decimals: 0,
    },
  ],
};

export const SILERO_VAD_SECTION: VadSectionDefinition = {
  id: 'silero',
  title: 'Détection intelligente',
  description: "Second filtre — l'IA reconnaît la voix humaine",
  parameters: [
    {
      key: 'silero.positiveSpeechThreshold',
      label: 'Début de la parole',
      description:
        "Le degré de certitude exigé pour que la cabine commence à écouter. Vers la droite, elle ne se déclenche que lorsqu'elle est sûre qu'il s'agit d'une voix : moins de faux départs, mais une voix très discrète peut être manquée.",
      sliderMinLabel: 'Au moindre son',
      sliderMaxLabel: "Quand c'est sûr",
      min: 0,
      max: 1,
      step: 0.01,
      defaultValue: 0.5,
      decimals: 2,
    },
    {
      key: 'silero.negativeSpeechThreshold',
      label: 'Fin de la parole',
      description:
        'Le moment où la cabine considère que la personne a fini de parler. Vers la gauche, elle attend longtemps avant de conclure ; vers la droite, elle termine dès que la voix faiblit.',
      sliderMinLabel: 'Attend davantage',
      sliderMaxLabel: 'Termine vite',
      min: 0,
      max: 1,
      step: 0.01,
      defaultValue: 0.35,
      decimals: 2,
    },
    {
      key: 'silero.minSpeechFrames',
      label: 'Confirmation de la voix',
      description:
        "Le nombre de petits extraits de voix d'affilée exigés avant de démarrer. Vers la droite, les claquements et bruits brefs sont ignorés, mais la cabine met un peu plus de temps à répondre.",
      sliderMinLabel: 'Répond vite',
      sliderMaxLabel: 'Très prudent',
      min: 1,
      max: 30,
      step: 1,
      defaultValue: 3,
      decimals: 0,
    },
    {
      key: 'silero.redemptionFrames',
      label: 'Tolérance aux pauses',
      description:
        'La durée de silence acceptée avant de couper. Vers la droite, les hésitations et les pauses ne coupent pas la phrase ; vers la gauche, la cabine conclut dès la première pause.',
      sliderMinLabel: 'Coupe vite',
      sliderMaxLabel: 'Laisse finir',
      min: 1,
      max: 100,
      step: 1,
      defaultValue: 8,
      decimals: 0,
    },
  ],
};

export const VAD_SECTIONS: readonly VadSectionDefinition[] = [
  VOLUME_VAD_SECTION,
  SILERO_VAD_SECTION,
];

const ALL_PARAMETERS: readonly VadParameterDefinition[] = [
  ...VOLUME_VAD_SECTION.parameters,
  ...SILERO_VAD_SECTION.parameters,
];

export interface VadPreset {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly values: VadValues;
}

/**
 * Environment presets, ordered from most permissive (quiet rooms)
 * to most selective (noisy job fairs with a close-talk headset).
 */
export const VAD_PRESETS: readonly VadPreset[] = [
  {
    id: 'quiet',
    label: 'Sensible',
    description:
      'Lieu silencieux — la cabine entend même les voix discrètes et laisse le temps de répondre.',
    values: {
      'volume.threshold': -70,
      'volume.history': 8,
      'silero.positiveSpeechThreshold': 0.2,
      'silero.negativeSpeechThreshold': 0.1,
      'silero.minSpeechFrames': 2,
      'silero.redemptionFrames': 24,
    },
  },
  {
    id: 'balanced',
    label: 'Équilibré',
    description: 'Un peu de bruit autour — la voix passe quand même.',
    values: {
      'volume.threshold': -58,
      'volume.history': 5,
      'silero.positiveSpeechThreshold': 0.35,
      'silero.negativeSpeechThreshold': 0.2,
      'silero.minSpeechFrames': 3,
      'silero.redemptionFrames': 16,
    },
  },
  {
    id: 'mall-booth',
    label: 'Centre commercial',
    description: 'Cabine en lieu passant — filtre le bruit qui traverse la porte.',
    values: {
      'volume.threshold': -50,
      'volume.history': 5,
      'silero.positiveSpeechThreshold': 0.55,
      'silero.negativeSpeechThreshold': 0.4,
      'silero.minSpeechFrames': 4,
      'silero.redemptionFrames': 20,
    },
  },
  {
    id: 'cibli-go',
    label: 'CibliGO',
    description:
      "Casque proche de la bouche (Logitech G Pro, Razer BlackShark V2 X) en salon ou jobdating bruyant — n'écoute que la personne au micro.",
    values: {
      // Close-talk headset mic: voice is loud and clear, so the volume filter
      // can stay moderate (no cut-off words) and the AI can confirm faster.
      'volume.threshold': -50,
      'volume.history': 4,
      'silero.positiveSpeechThreshold': 0.55,
      'silero.negativeSpeechThreshold': 0.4,
      'silero.minSpeechFrames': 4,
      'silero.redemptionFrames': 16,
    },
  },
];

export function getVadParameter(key: string): VadParameterDefinition | undefined {
  return ALL_PARAMETERS.find(parameter => parameter.key === key);
}

export function clampVadValue(definition: VadParameterDefinition, value: number): number {
  const clamped = Math.min(definition.max, Math.max(definition.min, value));
  return Number(clamped.toFixed(definition.decimals));
}

export function formatVadValue(definition: VadParameterDefinition, value: number): string {
  return value.toFixed(definition.decimals);
}

export function getDefaultVadValues(): VadValues {
  const values: VadValues = {};
  for (const parameter of ALL_PARAMETERS) {
    values[parameter.key] = parameter.defaultValue;
  }
  return values;
}

const VAD_STORAGE_KEY = 'cibli.vad-values';

/**
 * Load persisted VAD values from localStorage (survives session reset
 * and app reload). Unknown/out-of-range values fall back to defaults.
 */
export function loadVadValues(): VadValues {
  const values = getDefaultVadValues();
  try {
    const raw = localStorage.getItem(VAD_STORAGE_KEY);
    if (!raw) return values;
    const stored: unknown = JSON.parse(raw);
    if (typeof stored !== 'object' || stored === null) return values;
    for (const parameter of ALL_PARAMETERS) {
      const candidate = (stored as Record<string, unknown>)[parameter.key];
      if (typeof candidate === 'number' && Number.isFinite(candidate)) {
        values[parameter.key] = clampVadValue(parameter, candidate);
      }
    }
  } catch {
    // localStorage unavailable (private mode) or corrupt data → defaults
  }
  return values;
}

export function saveVadValues(values: VadValues): void {
  try {
    localStorage.setItem(VAD_STORAGE_KEY, JSON.stringify(values));
  } catch {
    // localStorage unavailable — settings stay in memory only
  }
}

export function validateVadValues(values: VadValues): readonly string[] {
  const errors: string[] = [];
  const positive = values['silero.positiveSpeechThreshold'];
  const negative = values['silero.negativeSpeechThreshold'];
  if (positive !== undefined && negative !== undefined && positive <= negative) {
    errors.push(
      "« Début de la parole » doit être réglé plus à droite que « Fin de la parole », sinon la cabine ne sait plus quand la personne s'arrête.",
    );
  }
  return errors;
}
